import { useEffect, useState } from "react";
import { Star, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

function Stars({ n = 5 }) {
  return (
    <div className="flex gap-0.5" aria-label={`${n} su 5 stelle`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={15} className={i <= n ? "fill-[#FBBC04] text-[#FBBC04]" : "fill-none text-ink/25"} strokeWidth={1.5} />
      ))}
    </div>
  );
}

const GoogleG = () => (
  <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.9 2.4 30.3 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-2.8-.4-4.1H24v7.4h12.7c-.3 2.1-1.6 5.3-4.7 7.4l7.2 5.6c4.3-4 6.9-9.9 6.9-16.3z" />
    <path fill="#FBBC05" d="M10.4 28.7c-.5-1.5-.8-3.1-.8-4.7s.3-3.2.8-4.7l-7.8-6.1C1 16.3 0 20 0 24s1 7.7 2.6 10.8l7.8-6.1z" />
    <path fill="#34A853" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7.2-5.6c-2 1.3-4.6 2.3-8.7 2.3-6.3 0-11.7-3.7-13.6-9.1l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
  </svg>
);

export default function GoogleReviews() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/reviews/google")
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  if (error || (data && (!data.reviews || data.reviews.length === 0))) return null;

  return (
    <section className="bg-linen py-24 lg:py-32" data-testid="google-reviews-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-claret mb-4">Dicono di noi</p>
            <h2 className="font-serif-display text-5xl lg:text-6xl leading-none">Le recensioni<br />dei nostri clienti.</h2>
          </div>
          {data && (
            <div className="flex items-center gap-4" data-testid="reviews-summary">
              <div className="flex items-center gap-2"><GoogleG /><span className="text-sm uppercase tracking-wide text-ink-soft">Recensioni Google</span></div>
              <div className="flex items-center gap-2">
                <span className="font-serif-display text-3xl leading-none">{Number(data.rating).toFixed(1)}</span>
                <div>
                  <Stars n={Math.round(data.rating)} />
                  <span className="text-xs text-ink-soft">{data.totalRatings} recensioni</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {!data ? (
          <div className="grid md:grid-cols-3 gap-6">
            {[0, 1, 2].map((i) => <div key={i} className="h-48 bg-white/60 border border-ink/8 animate-pulse" />)}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {data.reviews.map((r, i) => (
              <a
                key={i}
                href={r.googleMapsUri || data.googleMapsUri}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`review-card-${i}`}
                className="group flex flex-col bg-white border border-ink/10 p-7 hover:border-claret transition-colors duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  {r.photoUrl
                    ? <img src={r.photoUrl} alt={r.authorName} referrerPolicy="no-referrer" className="w-11 h-11 rounded-full object-cover" />
                    : <span className="w-11 h-11 rounded-full bg-claret text-bone flex items-center justify-center font-serif-display text-lg">{r.authorName.slice(0, 1)}</span>}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{r.authorName}</p>
                    <p className="text-xs text-ink-soft">{r.relativeTime}</p>
                  </div>
                  <GoogleG />
                </div>
                <Stars n={r.rating || 5} />
                <p className="text-ink-soft leading-relaxed mt-3 text-[15px] line-clamp-5">{r.text}</p>
              </a>
            ))}
          </div>
        )}

        {data?.googleMapsUri && (
          <a href={data.googleMapsUri} target="_blank" rel="noopener noreferrer" data-testid="reviews-google-link" className="mt-10 inline-flex items-center gap-3 text-claret hover:text-claret-dark uppercase tracking-[0.15em] text-sm transition-colors">
            Vedi tutte le recensioni su Google <ArrowRight size={18} />
          </a>
        )}
      </div>
    </section>
  );
}
